// src/context/AuthContext.js
import React, { useContext, useState, useEffect, createContext } from 'react';
import { auth, db } from '../firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, collection, onSnapshot } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null); // Holds Firestore user data (role, etc.)
    const [loading, setLoading] = useState(true); // Only true on initial load

    async function register(email, password, displayName, isCompany = false) {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Update Firebase Auth profile
            await updateProfile(user, { displayName });

            // Create user document in Firestore
            const userDocRef = doc(db, 'users', user.uid);
            let newUserdata;

            if (isCompany) {
                // Logic for company registration
                const companyDocRef = doc(collection(db, 'companies')); // Create new company doc
                newUserdata = {
                    uid: user.uid,
                    displayName,
                    email,
                    role: 'company',
                    isApproved: false,
                    carSlots: 0,
                    companyProfileId: companyDocRef.id,
                    createdAt: serverTimestamp()
                };
                // Create the company profile document
                await setDoc(companyDocRef, {
                    ownerUid: user.uid,
                    companyName: displayName,
                    companyPhone: '',
                    isActive: true,
                    createdAt: serverTimestamp()
                });
            } else {
                // Logic for regular customer registration
                newUserdata = {
                    uid: user.uid,
                    displayName,
                    email,
                    role: 'customer',
                    createdAt: serverTimestamp()
                };
            }
            await setDoc(userDocRef, newUserdata);
            return user;
        } catch (error) {
            console.error("Full registration process error:", error);
            throw error;
        }
    }

    async function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        setUserData(null);
        return signOut(auth);
    }

    const refreshUserData = async () => {
        if (!auth.currentUser) {
            setUserData(null);
            return null;
        }
        try {
            const userDocRef = doc(db, 'users', auth.currentUser.uid);
            const docSnap = await getDoc(userDocRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setUserData(data);
                return data;
            }
        } catch (err) {
            console.error("Error manually refreshing user data:", err);
        }
        return null;
    };

    useEffect(() => {
        let userDocUnsubscribe = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            if (userDocUnsubscribe) {
                userDocUnsubscribe();
                userDocUnsubscribe = null;
            }

            if (user) {
                setCurrentUser(user);
                const userDocRef = doc(db, 'users', user.uid);

                // Real-time listener for user document changes
                userDocUnsubscribe = onSnapshot(userDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setUserData(docSnap.data());
                    } else {
                        setUserData(null);
                        console.error("User document not found in Firestore for UID:", user.uid);
                    }
                    setLoading(false);
                }, (error) => {
                    console.error("Firestore user snapshot error:", error);
                    setLoading(false);
                });
            } else {
                setCurrentUser(null);
                setUserData(null);
                setLoading(false);
            }
        });

        return () => {
            if (userDocUnsubscribe) userDocUnsubscribe();
            unsubscribeAuth();
        };
    }, []);

    const value = {
        currentUser,
        userData, // Expose user data from firestore
        loading, // Expose loading state
        register,
        login,
        logout,
        refreshUserData,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}